import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import strings from 'strings/strings.json';
import useModifyRequest from 'hooks/useModifyRequest';
import { CourseSchedule } from 'models/CourseSchedule';
import useGetRequest from 'hooks/useGetRequest';
import { logError } from 'lib/debug-helpers';
import Notification, { NotificationType } from '../../components/notifiers/Notification';
import MainSection, { MainSectionWithSmallGaps } from '../../components/sections/MainSection';
import Header from 'components/sections/Header';
import Button from 'components/buttons/Button';

export type EnrollmentResponse = {
    enrollmentId: string;
    enrollmentStatus: 'Already enrolled' | 'New enrollment' | 'Cancelled' | 'Course full';
};

export default function EnrollPage(): JSX.Element {
    const navigate = useNavigate();
    const { courseScheduleId } = useParams();

    const [enrolledStatus, setEnrolledStatus] = React.useState<
        'Already enrolled' | 'New enrollment' | 'Cancelled' | 'Course full' | 'Error' | null
    >(null);
    const [enrollmentId, setEnrollmentId] = React.useState<string | null>(null);

    const { data: courseSchedule, loading } = useGetRequest<CourseSchedule>(
        `courses/learner/schedule-overview/${courseScheduleId}`,
    );
    const { modifyData: enrol } = useModifyRequest<{}, EnrollmentResponse>(
        `courses/enroll/${courseScheduleId}`,
        'POST',
    );

    React.useEffect(() => {
        async function enroll() {
            const response = await enrol({});
            const { errors, value } = response;

            if (value) {
                setEnrolledStatus(value.enrollmentStatus);
                switch (value.enrollmentStatus) {
                    case 'New enrollment':
                    case 'Already enrolled':
                        setEnrolledStatus(value.enrollmentStatus);
                        setEnrollmentId(value.enrollmentId);
                        break;
                    default:
                        break;
                }
            }

            if (errors) {
                logError(errors);
                setEnrolledStatus('Error');
            }
        }

        if (!courseSchedule) return;

        if (!enrolledStatus) enroll().then();
    }, [courseSchedule]); // eslint-disable-line

    if (loading) return <MainSection>loading course...</MainSection>;

    if (!courseSchedule)
        return (
            <MainSection>
                <Notification
                    notificationType={NotificationType.DANGER}
                    title={strings.enrollPage.courseNotFoundTitle}
                    description={strings.enrollPage.courseNotFoundDescription}
                />
            </MainSection>
        );

    return (
        <>
            <Header title={courseSchedule.course.title} />
            <MainSectionWithSmallGaps>
                {!enrolledStatus && <p>Enrolling in course, please wait...</p>}

                {enrolledStatus === 'New enrollment' && enrollmentId && (
                    <>
                        <Notification
                            notificationType={NotificationType.SUCCESS}
                            title={`${strings.enrollPage.saveSuccessTitle}${courseSchedule.course.title}!`}
                        />
                        <Button
                            title={strings.enrollPage.goToCourseButtonTitle}
                            aria={strings.enrollPage.goToCourseButtonTitleAria}
                            onClick={() => navigate(`/learner/courses/${enrollmentId}`)}
                        />
                    </>
                )}

                {enrolledStatus === 'Already enrolled' && enrollmentId && (
                    <>
                        <Notification
                            notificationType={NotificationType.WARNING}
                            title={strings.enrollPage.alreadyEnrolledTitle}
                        />
                        <Button
                            title={strings.enrollPage.goToCourseButtonTitle}
                            aria={strings.enrollPage.goToCourseButtonTitleAria}
                            onClick={() => navigate(`/learner/courses/${enrollmentId}`)}
                        />
                    </>
                )}

                {enrolledStatus === 'Course full' && (
                    <Notification
                        notificationType={NotificationType.WARNING}
                        title={strings.enrollPage.courseFullTitle}
                        description={strings.enrollPage.courseFullDescription}
                    />
                )}

                {enrolledStatus === 'Cancelled' && (
                    <Notification
                        notificationType={NotificationType.WARNING}
                        title={strings.enrollPage.enrollmentCancelledTitle}
                        description={strings.enrollPage.enrollmentCancelledDescription}
                    />
                )}

                {enrolledStatus === 'Error' && (
                    <Notification
                        notificationType={NotificationType.DANGER}
                        title={strings.enrollPage.enrolFailTitle}
                    />
                )}
            </MainSectionWithSmallGaps>
        </>
    );
}
